import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import "bootstrap/dist/css/bootstrap.min.css";
import "./index.css";
import "./styles/reportPageHeaders.css";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import {
  setAxiosNavigator,
  FORCE_LOGOUT_EVENT,
} from "./utils/axiosInstance";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Auth from "./components/Layouts/Auth";
import SuperAdmin from "./components/Layouts/SuperAdmin";
import Admin from "./components/Layouts/Admin";
import Client from "./components/Layouts/Client";
import StaffMember from "./components/Layouts/StaffMember";
import Plan from "./Views/Admin/Plan/Index";
import PlanPurchase from "./Views/Admin/Plan/PlanPurchase";
import { Toaster } from "react-hot-toast";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "leaflet/dist/leaflet.css";
import "./components/Toast/style.css";
import "./components/Toast/Undo.css";
import InvoicePayment from "./Views/Admin/Invoice/InvoiceLivePayment";
import LinkPayment from "./Views/Admin/Client/RecurringPayments/LinkPayment";
import { StaffProvider } from "./components/StaffData/Staffdata";
import { UserTypeProvider } from "./contexts/UserTypeContext";
import { DejavooPaymentProvider } from "./contexts/DejavooPaymentContext";
import { AuthProvider } from "./contexts/AuthContext";
import Donor from "./components/Layouts/Donor";
import ProtectedRoute from "./components/ProtectedRoute";
import AIChat from "./components/ChatBot/AiChat";
import PublicDonation from "./Views/Public/PublicDonation";
import NewsletterUnsubscribe from "./Views/Public/NewsletterUnsubscribe";
import TermsOfService from "./Views/Public/TermsOfService";
import PrivacyPolicy from "./Views/Public/PrivacyPolicy";
import ErrorBoundary from "./components/ErrorBoundary/ErrorBoundary";
import { suppressAllErrors } from "./utils/errorSuppression";
import { restoreAuthFromHash } from "./utils/authRestore";

/** Aligns MUI with `index.css` Cloud Job Manager style guide tokens */
const theme = createTheme({
  typography: {
    fontFamily: '"Inter", "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h2: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h3: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Poppins", sans-serif',
      fontWeight: 600,
    },
    subtitle1: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
    },
    subtitle2: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 500,
    },
    body1: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
    },
    body2: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
    },
    button: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
      fontWeight: 600,
      textTransform: "none",
    },
    caption: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
    },
    overline: {
      fontFamily: '"Inter", "Segoe UI", sans-serif',
    },
  },
  palette: {
    primary: {
      main: "#2563eb",
      light: "#dbeafe",
      dark: "#1d4ed8",
    },
    secondary: {
      main: "#d4943a",
      light: "#fef3e2",
      dark: "#b45309",
    },
    error: {
      main: "#dc2626",
      light: "#fee2e2",
    },
    success: {
      main: "#059669",
      light: "#d1fae5",
    },
    warning: {
      main: "#d97706",
      light: "#fef9c3",
    },
    info: {
      main: "#2563eb",
      light: "#dbeafe",
    },
    text: {
      primary: "#1f2937",
      secondary: "#6b7280",
      disabled: "#9ca3af",
    },
    divider: "#e5e7eb",
    background: {
      default: "#f7f8fa",
      paper: "#ffffff",
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiChip: {
      styleOverrides: {
        sizeSmall: {
          fontFamily: '"Inter", "Segoe UI", sans-serif',
          fontWeight: 600,
          fontSize: "12px",
          lineHeight: 1.25,
          height: "auto",
          minHeight: 24,
          padding: "4px 12px",
          borderRadius: "9999px",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

// SECURITY HISTORY (2026-04-20 → 2026-04-21): the prototype-freeze
// mitigation for the xlsx prototype-pollution CVE (GHSA-4r6h-8v6p-xvw6)
// was tried and reverted — d3-color (via recharts) writes to
// prototype.constructor at module init, which threw on frozen
// Object.prototype and killed every dashboard with a chart.
//
// The xlsx dependency itself has since been removed. All spreadsheet
// reads/writes now go through src/utils/xlsxHelpers.js, which wraps
// exceljs. Nothing in the tree should be pulling in `xlsx` — if it
// comes back, the CVE is open again.

// Bridges React Router's `navigate` into the axios instance so the 401
// interceptor can do SPA navigation instead of `window.location.href`. Also
// listens for force-logout events any module can dispatch — the goal is no
// hard reloads anywhere; loading skeletons handle the visual transition.
const AxiosNavigatorBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    setAxiosNavigator(navigate);
    const handleForceLogout = () => {
      navigate("/auth/login", { replace: true });
    };
    window.addEventListener(FORCE_LOGOUT_EVENT, handleForceLogout);
    return () => {
      setAxiosNavigator(null);
      window.removeEventListener(FORCE_LOGOUT_EVENT, handleForceLogout);
    };
  }, [navigate]);
  return null;
};

// Restore auth data before rendering
restoreAuthFromHash();

// Initialize error suppression - suppresses all errors from screen and console
suppressAllErrors();

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <ErrorBoundary>
    <BrowserRouter>
      <AuthProvider>
      <UserTypeProvider>
          <DejavooPaymentProvider>
          <StaffProvider>
            <AIChat />
            <ThemeProvider theme={theme}>
            <AxiosNavigatorBridge />
            <React.Suspense
              fallback={
                <div
                  style={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <style>{`@keyframes cms-spin-top { to { transform: rotate(360deg); } }`}</style>
                  <div
                    aria-label="Loading"
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      border: "4px solid rgba(15,43,70,0.12)",
                      borderTopColor: "#0f2b46",
                      animation: "cms-spin-top 0.9s linear infinite",
                    }}
                  />
                </div>
              }
            >
            <Routes>
            <Route path="/auth/*" element={<Auth />} />

            {/* SuperAdmin Routes - Protected */}
            <Route
              path="/superadmin/*"
              element={
                <ProtectedRoute requiredRole="SuperAdmin" tokenKey="adminToken">
                  <SuperAdmin />
                </ProtectedRoute>
              }
            />

            {/* Customer Routes - Protected */}
            {/* Customer Routes - Protected */}
            <Route
              path="/:CompanyUrl/customers/*"
              element={
                <ProtectedRoute
                  requiredRole="Customer"
                  tokenKey="customerToken"
                >
                  <Client />
                </ProtectedRoute>
              }
            />

            {/* Donor Routes - Protected */}
            <Route
              path="/:CompanyUrl/donors/*"
              element={
                <ProtectedRoute requiredRole="Customer" tokenKey="donorToken">
                  <Donor />
                </ProtectedRoute>
              }
            />

            {/* Staff Member Routes - Protected */}
            <Route
              path="/:CompanyUrl/staff-member/*"
              element={
                <ProtectedRoute requiredRole="Worker" tokenKey="workerToken">
                  <StaffMember />
                </ProtectedRoute>
              }
            />

            {/* Admin/Company Routes - Protected */}
            <Route
              path="/:CompanyUrl/*"
              element={
                <ProtectedRoute requiredRole="Company" tokenKey="adminToken">
                  <Admin />
                </ProtectedRoute>
              }
            />

            {/* Public Routes */}
            <Route path="/donation" element={<PublicDonation />} />
            <Route path="/unsubscribe" element={<NewsletterUnsubscribe />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/plan" element={<Plan />} />
            <Route path="/plan-purchase" element={<PlanPurchase />} />
            <Route path="/" element={<Navigate to="/auth/login" replace />} />
            <Route path="/InvoicePayment" element={<InvoicePayment />} />
            <Route path="/LinkPayment" element={<LinkPayment />} exact />
          </Routes>
          </React.Suspense>
          <Toaster
            position="top-center"
            containerClassName="rht-toaster-root"
            toastOptions={{
              duration: 3000,
              style: {
                background: "var(--toast-dark-bg)",
                color: "var(--text-white-color)",
                maxWidth: "90vw",
                minWidth: "300px",
                fontSize: "14px",
                padding: "16px",
                borderRadius: "8px",
                boxShadow: "var(--shadow-md)",
              },
              success: {
                style: {
                  background: "var(--green)",
                },
              },
              error: {
                style: {
                  background: "var(--red)",
                },
              },
              loading: {
                style: {
                  background: "var(--blue-primary)",
                },
              },
            }}
          />
          <ToastContainer
            position="top-center"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            style={{
              zIndex: 9999,
            }}
            toastStyle={{
              background: "var(--bg-card)",
              color: "var(--text-primary)",
              maxWidth: "90vw",
              minWidth: "300px",
              fontSize: "14px",
              padding: "16px",
              borderRadius: "8px",
              boxShadow: "var(--shadow-md)",
              border: "1px solid var(--border)",
            }}
            toastClassName="responsive-toast"
          />
        </ThemeProvider>
      </StaffProvider>
          </DejavooPaymentProvider>
    </UserTypeProvider>
      </AuthProvider>
  </BrowserRouter>
  </ErrorBoundary>
);